import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaArrowLeft } from 'react-icons/fa';

import Loading from '../../../../components/Loading';
import Modal from '../../../../components/Modal/Modal';
import usePermissions from '../../../../components/Menu/MenuItems/hooks/usePermissions';
import { Container } from '../../../../styles/layout/Container';
import { EmptyState } from '../../../../styles/feedback/EmptyState';
import { TableWrapper } from '../../../../styles/table/TableWrapper';
import { Table } from '../../../../styles/table/Table';
import { ClickableRow } from '../../../../styles/ui/Link';
import {
   EntityActionBar,
   ActionBarSpacer,
} from '../../../../styles/ui/ActionBar';
import {
   CtaButton,
   DangerButton,
   GhostButton,
} from '../../../../styles/ui/Button';
import {
   getJobOpening,
   deleteJobOpening,
} from '../../../../services/hr/recruitment/jobOpeningsApi';
import { getOpeningReport } from '../../../../services/hr/recruitment/reportsApi';
import { listCandidates } from '../../../../services/hr/recruitment/candidatesApi';

import JobOpeningStatusBadge from '../components/JobOpeningStatusBadge';
import StageBadge from '../components/StageBadge';
import OpeningReportPanel from '../components/OpeningReportPanel';
import { JOB_OPENING_STATUS } from '../constants';
import { formatDateBr } from '../utils/format';
import { recruitmentErrorMessage } from '../utils/apiError';
import PullCandidatesModal from './components/PullCandidatesModal';
import {
   HeaderBar,
   HeaderMain,
   TitleRow,
   MetaLine,
   Card,
   CardHead,
   FactGrid,
   Fact,
   BackRow,
} from '../detailStyles';

// ---------------------------------------------------------------------------
// RH · Recrutamento — Ficha da vaga (handoff 0061 §5.3). Dados + status +
// candidatos da vaga (GET /candidates?job_opening_id=) + relatório do processo
// (GET /reports/opening/:id). Ações: editar / excluir (409 se houver candidatos).
// ---------------------------------------------------------------------------

const CANDIDATES_PAGE_SIZE = 100;

export default function OpeningShow() {
   const { id } = useParams();
   const navigate = useNavigate();
   const location = useLocation();
   const { hasPermission } = usePermissions();

   const canUpdate = hasPermission('hr:job_opening:update');
   const canDelete = hasPermission('hr:job_opening:delete');
   // Puxar candidato usa PATCH /candidates/:id/opening — mesma perm da ficha.
   const canPullCandidates = hasPermission('hr:candidate:update');

   const [opening, setOpening] = useState(null);
   const [candidates, setCandidates] = useState([]);
   const [report, setReport] = useState(null);
   const [isLoading, setIsLoading] = useState(true);
   const [notFound, setNotFound] = useState(false);

   const [deleteOpen, setDeleteOpen] = useState(false);
   const [isDeleting, setIsDeleting] = useState(false);
   const [pullOpen, setPullOpen] = useState(false);

   const fetchAll = useCallback(async () => {
      setIsLoading(true);
      try {
         const openingResult = await getJobOpening(id);
         if (!openingResult) {
            setNotFound(true);
            return;
         }
         setOpening(openingResult);

         // Candidatos e relatório são complementares — uma falha aqui não deve
         // derrubar a ficha da vaga. Cada um degrada pro seu vazio.
         const [candidatesResult, reportResult] = await Promise.all([
            listCandidates({
               jobOpeningId: id,
               perPage: CANDIDATES_PAGE_SIZE,
            }).catch(() => ({ candidates: [] })),
            getOpeningReport(id).catch(() => null),
         ]);
         setCandidates(candidatesResult.candidates ?? []);
         setReport(reportResult);
      } catch (err) {
         if (err?.response?.status === 404) {
            setNotFound(true);
         } else {
            toast.error(recruitmentErrorMessage(err, 'Erro ao carregar a vaga.'));
            setNotFound(true);
         }
      } finally {
         setIsLoading(false);
      }
   }, [id]);

   useEffect(() => {
      fetchAll();
   }, [fetchAll]);

   const handleBack = () => {
      if (location.key !== 'default') navigate(-1);
      else navigate('/hr/recruitment/openings');
   };

   const handleConfirmDelete = async () => {
      if (isDeleting) return;
      setIsDeleting(true);
      try {
         await deleteJobOpening(id);
         toast.success('Vaga excluída.');
         navigate('/hr/recruitment/openings');
      } catch (err) {
         toast.error(
            recruitmentErrorMessage(err, 'Não foi possível excluir a vaga.'),
         );
         setDeleteOpen(false);
      } finally {
         setIsDeleting(false);
      }
   };

   if (isLoading && !opening) {
      return (
         <Container>
            <Loading isLoading />
         </Container>
      );
   }

   if (notFound || !opening) {
      return (
         <Container>
            <BackRow>
               <GhostButton type="button" onClick={handleBack}>
                  <FaArrowLeft /> Voltar
               </GhostButton>
            </BackRow>
            <EmptyState
               title="Vaga não encontrada"
               description="Esta vaga não existe ou não pôde ser carregada."
            />
         </Container>
      );
   }

   const positionName = opening.job_position?.position_name;
   const title = opening.title || positionName || `Vaga #${opening.id}`;
   const hasCandidates = candidates.length > 0;
   // Só vaga ABERTA recebe candidato (guard job_opening_not_open do backend).
   const canReceiveCandidates =
      canPullCandidates && opening.status === JOB_OPENING_STATUS.ABERTA;

   return (
      <Container>
         <Loading isLoading={isLoading} />

         <BackRow>
            <GhostButton type="button" onClick={handleBack}>
               <FaArrowLeft /> Voltar
            </GhostButton>
         </BackRow>

         <HeaderBar>
            <HeaderMain>
               <div>
                  <TitleRow>
                     <h1>{title}</h1>
                     <JobOpeningStatusBadge value={opening.status} />
                  </TitleRow>
                  <MetaLine>
                     <span>{positionName ?? 'Cargo não informado'}</span>
                     <span className="dot">·</span>
                     <span>
                        {opening.openings_count ?? 1}{' '}
                        {(opening.openings_count ?? 1) === 1
                           ? 'vaga'
                           : 'vagas'}
                     </span>
                     <span className="dot">·</span>
                     <span>Aberta em {formatDateBr(opening.opened_at)}</span>
                  </MetaLine>
               </div>
            </HeaderMain>

            <EntityActionBar>
               {canDelete && (
                  <>
                     <DangerButton
                        type="button"
                        onClick={() => setDeleteOpen(true)}
                     >
                        Excluir
                     </DangerButton>
                     <ActionBarSpacer />
                  </>
               )}
               {canUpdate && (
                  <CtaButton
                     type="button"
                     onClick={() =>
                        navigate(`/hr/recruitment/openings/${opening.id}/edit`)
                     }
                  >
                     Editar
                  </CtaButton>
               )}
            </EntityActionBar>
         </HeaderBar>

         {/* ─── Dados da vaga ───────────────────────────────────────────── */}
         <Card>
            <CardHead>
               <h2>Dados da vaga</h2>
            </CardHead>
            <FactGrid>
               <Fact>
                  <div className="fact-label">Cargo</div>
                  <div className="fact-value">{positionName ?? '—'}</div>
               </Fact>
               <Fact>
                  <div className="fact-label">Nº de vagas</div>
                  <div className="fact-value">{opening.openings_count ?? 1}</div>
               </Fact>
               <Fact>
                  <div className="fact-label">Status</div>
                  <div className="fact-value">
                     <JobOpeningStatusBadge value={opening.status} />
                  </div>
               </Fact>
               <Fact>
                  <div className="fact-label">Aberta em</div>
                  <div className="fact-value muted">
                     {formatDateBr(opening.opened_at)}
                  </div>
               </Fact>
               <Fact>
                  <div className="fact-label">Encerrada em</div>
                  <div className="fact-value muted">
                     {formatDateBr(opening.closed_at)}
                  </div>
               </Fact>
            </FactGrid>

            {opening.notes && (
               <div style={{ marginTop: 14 }}>
                  <div
                     style={{
                        fontSize: 11,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.03em',
                        color: '#6b7280',
                     }}
                  >
                     Observações
                  </div>
                  <div
                     style={{
                        marginTop: 4,
                        fontSize: '0.9rem',
                        whiteSpace: 'pre-wrap',
                     }}
                  >
                     {opening.notes}
                  </div>
               </div>
            )}
         </Card>

         {/* ─── Relatório do processo ───────────────────────────────────── */}
         <Card>
            <CardHead>
               <h2>Relatório do processo</h2>
            </CardHead>
            <OpeningReportPanel report={report} />
         </Card>

         {/* ─── Candidatos na vaga ──────────────────────────────────────── */}
         <Card>
            <CardHead>
               <h2>
                  Candidatos{candidates.length ? ` (${candidates.length})` : ''}
               </h2>
               {canReceiveCandidates && (
                  <CtaButton type="button" onClick={() => setPullOpen(true)}>
                     + Puxar candidatos
                  </CtaButton>
               )}
            </CardHead>

            {hasCandidates ? (
               <TableWrapper>
                  <Table>
                     <thead>
                        <tr>
                           <th>Candidato</th>
                           <th>Cargo pretendido</th>
                           <th className="center">Etapa</th>
                        </tr>
                     </thead>
                     <tbody>
                        {candidates.map((candidate) => (
                           <ClickableRow
                              key={candidate.id}
                              onClick={() =>
                                 navigate(
                                    `/hr/recruitment/candidates/${candidate.id}`,
                                 )
                              }
                           >
                              <td>{candidate.full_name}</td>
                              <td>
                                 {candidate.desired_position?.position_name ??
                                    '—'}
                              </td>
                              <td className="center">
                                 <StageBadge value={candidate.stage} />
                              </td>
                           </ClickableRow>
                        ))}
                     </tbody>
                  </Table>
               </TableWrapper>
            ) : (
               <EmptyState
                  title="Nenhum candidato nesta vaga"
                  description={
                     canReceiveCandidates
                        ? 'Use "+ Puxar candidatos" para trazer candidatos do banco de currículos.'
                        : 'Puxe candidatos do banco de currículos para esta vaga.'
                  }
               />
            )}
         </Card>

         <PullCandidatesModal
            isOpen={pullOpen}
            onClose={() => setPullOpen(false)}
            opening={opening}
            onSaved={fetchAll}
         />

         <Modal
            isOpen={deleteOpen}
            title="Excluir vaga"
            onClose={() => setDeleteOpen(false)}
            maxWidth="440px"
            footer={
               <>
                  <GhostButton
                     type="button"
                     onClick={() => setDeleteOpen(false)}
                     disabled={isDeleting}
                  >
                     Cancelar
                  </GhostButton>
                  <DangerButton
                     type="button"
                     onClick={handleConfirmDelete}
                     disabled={isDeleting}
                  >
                     {isDeleting ? 'Excluindo…' : 'Excluir vaga'}
                  </DangerButton>
               </>
            }
         >
            <p style={{ margin: 0, fontSize: '0.9rem' }}>
               Tem certeza que deseja excluir <strong>{title}</strong>? Vagas com
               candidatos vinculados não podem ser excluídas.
            </p>
         </Modal>
      </Container>
   );
}
